import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/**
 * Read-only right-side drawer with a PDF preview and a few key facts.
 * `loadPdf` must return a promise resolving to an axios blob response.
 * Optional: `actions` renders extra header controls (before the close button);
 * `footer` renders below the PDF area (e.g. credit notes on an invoice).
 */
const QuickViewDrawer = ({ open, onClose, title, statusColor, statusLabel, facts = [], loadPdf, onOpenFull, openLabel, pdfErrorLabel, actions, footer }) => {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!open || !loadPdf) return undefined;
        let objectUrl = null;
        let cancelled = false;
        setLoading(true);
        setError(false);
        setPdfUrl(null);
        loadPdf()
            .then(res => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                setPdfUrl(objectUrl);
            })
            .catch(() => { if (!cancelled) setError(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100vw', md: 720 }, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 32px rgba(15,23,42,0.12)' } }}
            slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
        >
            <Box sx={{ height: 3, bgcolor: '#427898', flexShrink: 0 }} />

            {/* Header */}
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography noWrap sx={{ fontWeight: 700, fontSize: 16, color: '#0F172A' }}>{title}</Typography>
                {statusLabel && (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: statusColor }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: statusColor }}>{statusLabel}</Typography>
                    </Box>
                )}
                <Box sx={{ flex: 1 }} />
                {onOpenFull && (
                    <Button
                        size="small"
                        startIcon={<OpenInNewIcon sx={{ fontSize: '15px !important' }} />}
                        onClick={onOpenFull}
                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13, color: '#427898', flexShrink: 0, '&:hover': { bgcolor: '#F0F7FF' } }}
                    >
                        {openLabel}
                    </Button>
                )}
                {actions}
                <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8', flexShrink: 0 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Facts */}
            {facts.length > 0 && (
                <Box sx={{ px: 3, py: 1.75, borderBottom: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    {facts.map(f => (
                        <Box key={f.label} sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 10.5, color: '#94A3B8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                                {f.label}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: 13, fontWeight: 500, color: '#1E293B', mt: 0.25 }}>
                                {f.value ?? '—'}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* PDF */}
            <Box sx={{ flex: 1, bgcolor: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {loading ? (
                    <CircularProgress size={32} sx={{ color: '#427898' }} />
                ) : error ? (
                    <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>{pdfErrorLabel}</Typography>
                ) : pdfUrl ? (
                    <iframe src={pdfUrl} title={title} style={{ width: '100%', height: '100%', border: 'none' }} />
                ) : null}
            </Box>

            {footer && <Box sx={{ flexShrink: 0 }}>{footer}</Box>}
        </Drawer>
    );
};

export default QuickViewDrawer;
